import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/app/Header";
import WhatsAppButton from "@/components/app/WhatsAppButton";
import StudioPreviewModal from "@/components/app/StudioPreviewModal";
import { Button } from "@/components/ui/button";
import { Calendar, Sparkles, ArrowRight, MapPin, Clock } from "lucide-react";

export default function Landing() {
  const [settings, setSettings] = useState(null);
  const [studios, setStudios] = useState([]);
  const [previewStudio, setPreviewStudio] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/studios").then((r) => setStudios(r.data)).catch(() => {});
  }, []);

  const whatsappMsg = `Salve, vorrei avere informazioni o prenotare una visita.`;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24">
        {/* HERO */}
        <section className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="fade-up">
            <div className="overline mb-5">Osteopatia · Fisioterapia · Milano</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A231F] leading-[1.05]">
              Fissa la tua<br />
              <span className="italic font-normal text-[#2D4A3E]">prima visita</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5C7066] leading-relaxed max-w-md">
              {settings?.bio || "Trattamenti manuali su misura per liberarti dal dolore e tornare in movimento. Due studi a Milano, una sola attenzione: la tua."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/prenota" data-testid="cta-prenota" className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-xl bg-[#2D4A3E] text-white font-medium hover:bg-[#1A2F26] transition-colors duration-300">
                <Calendar size={18} strokeWidth={1.8} /> Prenota un appuntamento
              </Link>
              <Link to="/trattamenti" data-testid="cta-trattamenti" className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-xl border border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white transition-colors duration-300">
                <Sparkles size={18} strokeWidth={1.8} /> Informazioni sui trattamenti
              </Link>
            </div>
            <div className="mt-4">
              <WhatsAppButton number={settings?.whatsapp_number} message={whatsappMsg} className="w-full sm:w-auto" />
            </div>
          </div>

          <div className="fade-up d1 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden card-soft grain bg-[#F5F1E6]">
              {settings?.hero_image ? (
                <img
                  src={settings.hero_image}
                  alt="Professional therapist"
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>
            <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white card-soft px-5 py-4 max-w-[220px]">
              <div className="overline">Professionista</div>
              <div className="font-display text-base text-[#1A231F] mt-1">{settings?.professional_name || "Dr. Marco Bianchi"}</div>
              <div className="text-xs text-[#5C7066] mt-0.5">{settings?.qualification || "Osteopata D.O. · Fisioterapista"}</div>
            </div>
          </div>
        </section>

        {/* STUDIOS preview */}
        <section className="mt-24">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline mb-2">Sedi</div>
              <h2 className="font-display text-2xl sm:text-3xl text-[#1A231F]">Due studi a Milano</h2>
            </div>
            <Link to="/prenota" data-testid="cta-scegli-studio" className="hidden sm:inline-flex items-center gap-1 text-sm text-[#2D4A3E] hover:underline">
              Scegli lo studio <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {studios.map((s, i) => (
              <button
                type="button"
                key={s.id}
                data-testid={`studio-preview-${s.id}`}
                onClick={() => setPreviewStudio(s)}
                className={`card-soft p-6 text-left w-full cursor-pointer hover:border-[#8AA399] fade-up d${i+1}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-xl text-[#1A231F]">{s.name}</h3>
                    <div className="mt-1 flex items-center gap-1 text-sm text-[#5C7066]">
                      <MapPin size={14} /> {s.address}
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-[#8AA399] shrink-0 mt-1" />
                </div>
                <div className="mt-5 space-y-2">
                  {s.schedule.map((sc, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-[#5C7066]">
                      <Clock size={14} className="text-[#2D4A3E]"/>
                      <span>{["Lun","Mar","Mer","Gio","Ven","Sab","Dom"][sc.weekday]} · {sc.start} — ultimo {sc.last_start}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-xs text-[#2D4A3E] flex items-center gap-1">
                  Vedi mappa e prenota <ArrowRight size={12}/>
                </div>
              </button>
            ))}
          </div>
        </section>

        <StudioPreviewModal
          studio={previewStudio}
          open={!!previewStudio}
          onClose={() => setPreviewStudio(null)}
        />

        <footer className="mt-24 pt-10 border-t border-[#2D4A3E]/10 text-xs text-[#5C7066] flex flex-col sm:flex-row gap-2 sm:justify-between">
          <div>© {new Date().getFullYear()} {settings?.professional_name || "Studio Milano"}</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#2D4A3E]">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#2D4A3E]">Termini</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
